import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Bolt, CheckCircleOutline, DrawOutlined, ErrorOutline, FileUploadOutlined, QueryBuilder } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DateFormats, practiceStateSetupRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useProviderStatesSelectionMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AsyncButton, LightChip, Tooltip } from '@uplift/ui-v2';
import { UploadCollaborativeAgreementModal, collaborativeAgreementRejectionReasonText } from '@uplift/pages';

import { useGoLiveLocked } from '../../../../hooks';
import { LockedGoLiveButton } from '../GoLiveLocked';
import { PracticeStateRow, PracticeStateStatus } from './PracticeStatesTable.types';
import { ViewCollaborativeAgreementDocumentModal } from './ViewCollaborativeAgreementDocumentModal';


export type StateStatusCellProps = {
  value?: PracticeStateStatus;
  row: PracticeStateRow;
};

export const StateStatusCell: React.FC<StateStatusCellProps> = ({ value, row }) => {
  const history = useHistory();
  const { locked: goLiveLocked } = useGoLiveLocked(row.providerGoLiveAllowed);

  if (value === 'expired') {
    return <ExpiredChip expiration={row.licenseExpiration} />;
  }

  if (value === 'live')
    return (
      <LightChip
        color="success"
        label="Live"
        icon={<CheckCircleOutline fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'inReview')
    return (
      <LightChip
        color="default"
        label="In Review"
        icon={<QueryBuilder fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'inProgress')
    return (
      <LightChip
        color="default"
        label="In Progress"
        icon={<QueryBuilder fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'rejected') {
    return <RejectedActions row={row} />;
  }

  if (value === 'getStarted') {
    if (goLiveLocked) {
      return <LockedGoLiveButton label="Get Started" icon={<Bolt />} />;
    }

    return <GetStartedButton stateId={row.stateId} />;
  }

  if (value === 'goLive') {
    if (goLiveLocked) {
      return <LockedGoLiveButton label="Go Live" icon={<CheckCircleOutline />} />;
    }

    return <GoLiveButton stateId={row.stateId} />;
  }

  if (value === 'agreementUnsigned') {
    return (
      <Button
        size="small" color="secondary" variant="contained"
        onClick={() => {
          history.push(ROUTING_CONFIG.documentsCenter);
        }}
      >Sign Agreement</Button>
    );
  }

  if (value === 'agreementCounterSignRequired') {
    return (
      <Tooltip title="Agreement signed and waiting for counter signature by physician">
        <span>
          <LightChip
            color="warning"
            label="Awaiting Countersignature"
            icon={<DrawOutlined fontSize="small" />}
            size="medium"
          />
        </span>
      </Tooltip>
    );
  }

  return null;
};

type ExpiredChipProps = {
  expiration: string;
};

const ExpiredChip: React.FC<ExpiredChipProps> = ({ expiration }) => {
  const formatted = CalendarDateService.date(expiration).format(DateFormats['MMM D, YYYY']);

  return (
    <Tooltip title={`License expired on ${formatted}. Update the license expiration date to enable actions.`}>
      <span>
        <LightChip
          color="error"
          label="Expired"
          icon={<ErrorOutline fontSize="small" />}
          size="medium"
        />
      </span>
    </Tooltip>
  );
};

type RejectedActionsProps = {
  row: PracticeStateRow;
};

const RejectedActions: React.FC<RejectedActionsProps> = ({ row }) => {
  const [ modal, setModal ] = useState<'view' | 'upload'>();
  const me = useCurrentuser();
  const agreement = row.activeCollaborativeAgreement;

  if (!agreement) return null;

  return (
    <>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title={collaborativeAgreementRejectionReasonText(agreement.rejectionReason, agreement.rejectionDetails)}>
          <span>
            <LightChip
              color="error"
              label="Rejected"
              icon={<ErrorOutline fontSize="small" />}
              size="medium"
            />
          </span>
        </Tooltip>
        <Button variant="contained" color="secondary" size="small" startIcon={<FileUploadOutlined />} onClick={() => setModal('view')}>
          Resubmit
        </Button>
      </Box>
      {modal === 'view' && (
        <ViewCollaborativeAgreementDocumentModal
          agreement={agreement}
          providerId={me.id}
          stateName={row.stateName}
          onUploadNew={() => setModal('upload')}
          onClose={() => setModal(undefined)}
        />
      )}
      {modal === 'upload' && (
        <UploadCollaborativeAgreementModal
          onClose={() => setModal(undefined)}
          license={{
            id: row.id,
            stateId: row.stateId,
            userId: me.id
          }}
        />
      )}
    </>
  );
};

type GetStartedButtonProps = {
  stateId: string;
};

const GetStartedButton: React.FC<GetStartedButtonProps> = ({ stateId }) => {
  const history = useHistory();

  return (
    <Button
      variant="contained"
      color="secondary"
      size="small"
      startIcon={<Bolt />}
      onClick={() => history.push(practiceStateSetupRoute.createLink({ stateId }))}
    >
      Get Started
    </Button>
  );
};

type GoLiveButtonProps = {
  stateId: string;
};

const GoLiveButton: React.FC<GoLiveButtonProps> = ({ stateId }) => {
  const me = useCurrentuser();
  const providerStatesSelectionMutation = useProviderStatesSelectionMutation(me?.id);

  const handleGoLive = async () => {
    const stateIds = [ stateId ];

    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: false });
    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: true });
  };


  return (
    <AsyncButton
      variant="contained"
      color="secondary"
      size="small"
      startIcon={<CheckCircleOutline />}
      onClick={handleGoLive}
    >
      Go Live
    </AsyncButton>
  );
};
